import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { InfluencerUpgradeFormEnum } from "./config";
import { Form, Input, Radio, Image, Checkbox } from "antd";
import { CloseBgIcon, DeleteIcon, PlusIcon, UploadIcon } from "../icons";
import UploadImages from "../CommonUploadImages";

export default function InfluencerUpgradeForm({
  value,
  isPreview = false,
  listValue = [],
  form,
  requireCreatorRegistration = false,
  requireCreatorRegistrationName = 'requireCreatorRegistration',
}: {
  value: InfluencerUpgradeFormEnum;
  isPreview?: boolean;
  listValue?: any[];
  form?: any;
  requireCreatorRegistration?: boolean;
  requireCreatorRegistrationName?: string | string[];
}) {
  const { t } = useTranslation();
  // 达人会员升级
  const [creatorAccountNeeded, setCreatorAccountNeeded] = useState<any>(null);
  useEffect(() => {
    setCreatorAccountNeeded(
      value || InfluencerUpgradeFormEnum.NO_ACCESS_NEEDED
    );
  }, [value]);

  useEffect(() => {
    form?.setFieldsValue({
      creatorAccountNeeded: creatorAccountNeeded,
    });
  }, [creatorAccountNeeded]);

  const creatorAccountInfoFields = Form.useWatch('creatorAccountInfoFields', form) || [];

  return (
    <div className="mt-6">
      <div className="self-stretch justify-start text-font&icon-primary text-base font-semibold font-['DM_Sans'] leading-6">
        {t("components.influencerUpgradeForm.title")}
      </div>
      <div className="flex items-center mt-3 gap-6">
        {isPreview ? (
          <Radio.Group
            value={creatorAccountNeeded}
            onChange={(e) => {
              const newValue = e.target.value as InfluencerUpgradeFormEnum;
              setCreatorAccountNeeded(newValue);
            }}
            disabled={true}
          >
            <Radio value={InfluencerUpgradeFormEnum.ENABLE_ACCESS_ADD_CREDITS}>
              {t("components.influencerUpgradeForm.enableAccessAddCredits")}
            </Radio>
            <Radio value={InfluencerUpgradeFormEnum.NO_ACCESS_NEEDED}>
              {t("components.influencerUpgradeForm.noAccessNeeded")}
            </Radio>
          </Radio.Group>
        ) : (
          <Form.Item
            name="creatorAccountNeeded"
            rules={[
              {
                required: true,
                message: t(
                  "components.influencerUpgradeForm.validationMessage"
                ),
              },
            ]}
            className="mb-0"
            initialValue={InfluencerUpgradeFormEnum.NO_ACCESS_NEEDED}
          >
            <Radio.Group
              value={creatorAccountNeeded}
              onChange={(e) => {
                const newValue = e.target.value as InfluencerUpgradeFormEnum;
                setCreatorAccountNeeded(newValue);
              }}
              disabled={isPreview}
            >
              <Radio
                value={InfluencerUpgradeFormEnum.ENABLE_ACCESS_ADD_CREDITS}
              >
                {t("components.influencerUpgradeForm.enableAccessAddCredits")}
              </Radio>
              <Radio value={InfluencerUpgradeFormEnum.NO_ACCESS_NEEDED}>
                {t("components.influencerUpgradeForm.noAccessNeeded")}
              </Radio>
            </Radio.Group>
          </Form.Item>
        )}
      </div>
      {creatorAccountNeeded ===
        InfluencerUpgradeFormEnum.ENABLE_ACCESS_ADD_CREDITS && (
          <div className="rounded-[16px] mt-3">
            <div className="w-full">
              {isPreview ? (
                // 预览模式：直接渲染 listValue
                listValue && listValue.length > 0 ? (
                  <div className="flex flex-col gap-2">
                    {listValue.map((item: any, index: number) => (
                      <div key={index} className="w-full flex flex-col gap-2">
                        <div className="flex items-center gap-4 w-full">
                          <div className="flex flex-col justify-start items-start gap-1 w-[calc((100%-32px)/3)]">
                            <div className="self-stretch justify-start">
                              <span className="text-font&icon-secondary text-xs font-medium font-['DM_Sans'] leading-4">
                                {t(
                                  "components.influencerUpgradeForm.informationRequiredLabel"
                                )}
                              </span>
                            </div>
                          </div>
                          <div className="justify-start text-font&icon-secondary text-xs font-medium font-['DM_Sans'] leading-4 w-[calc((100%-32px)/3)]">
                            {t(
                              "components.influencerUpgradeForm.instructionLabel"
                            )}
                          </div>
                          {
                            item?.images?.length > 0 && (
                              <div className="justify-start text-font&icon-secondary text-xs font-medium font-['DM_Sans'] leading-4 w-[calc((100%-32px)/3)]">
                                {t("components.influencerUpgradeForm.photos")}
                              </div>
                            )
                          }
                        </div>
                        <div className="flex items-start gap-4 w-full">
                          <div className="w-[calc((100%-32px)/3)]">
                            <div className="justify-start text-text-color-primary text-sm font-medium font-['Inter']">
                              {item.field}
                            </div>
                          </div>
                          <div className="w-[calc((100%-32px)/3)]">
                            <div className="self-stretch justify-start text-font&icon-primary text-sm font-medium font-['Inter']">
                              {item.description}
                            </div>
                          </div>
                          {
                            item?.images?.length > 0 && (
                              <div className="flex flex-col gap-2 w-[calc((100%-32px)/3)]">
                                {
                                  item?.images.map((image: any, index: number) => (
                                    <div key={index} className="flex items-center gap-1">
                                      <Image src={image.src} alt={image.fileName} width={18} height={18} className="object-cover rounded" />
                                      <div className="justify-start text-font&icon-primary text-sm font-medium font-['Inter']">{image?.fileName}</div>
                                    </div>
                                  ))
                                }
                              </div>
                            )
                          }
                        </div>
                      </div>
                    ))}
                    <div className="flex items-center gap-2 mt-4">
                      <Checkbox checked={requireCreatorRegistration} disabled={true} />
                      <div className="justify-start text-Colors-Text-text-secondary-(700) text-sm font-medium font-['DM_Sans'] leading-5">{t('campaignDetail.otherInformationPreview.requireCreatorRegistrationDesc')}</div>
                    </div>
                  </div>
                ) : null
              ) : (
                // 编辑模式：使用 Form.List
                <>
                  <Form.List
                    name="creatorAccountInfoFields"
                    initialValue={
                      listValue && listValue.length > 0
                        ? listValue
                        : [{ field: "", description: "" }]
                    }
                  >
                    {(fields, { add, remove }) => {
                      return (
                        <div className="flex flex-col gap-2">
                          {fields.map((field: any, index) => {
                            const images = creatorAccountInfoFields[field.name]?.images || [];
                            return (
                              <div
                                key={field.key}
                                className="w-full flex flex-col gap-2"
                              >
                                <div className="flex flex-col items-start gap-3 w-full">
                                  <div className="flex flex-col gap-6 bg-[#FAFAF9] p-4 rounded-md w-full">
                                    <div className="flex flex-col gap-2">
                                      <div className="flex items-center justify-between">
                                        <div className="justify-start">
                                          <span className="text-font&icon-secondary text-xs font-medium font-['DM_Sans'] leading-4">
                                            {t(
                                              "components.influencerUpgradeForm.informationRequiredLabel"
                                            )}
                                          </span>
                                          <span className="text-[#DC2626] text-xs font-medium font-['DM_Sans'] leading-4">
                                            *
                                          </span>
                                        </div>
                                        {fields.length > 1 && (
                                          <span
                                            className="cursor-pointer flex justify-center items-center"
                                            onClick={() => remove(field.name)}
                                          >
                                            <DeleteIcon className="w-4 h-4 text-primary" />
                                          </span>
                                        )}
                                      </div>
                                      <Form.Item
                                        name={[field.name, "field"]}
                                        className="mb-0"
                                        rules={[
                                          {
                                            required: true,
                                            message: t(
                                              "components.influencerUpgradeForm.informationRequiredValidation"
                                            ),
                                          },
                                        ]}
                                      >
                                        <Input
                                          placeholder={t(
                                            "components.influencerUpgradeForm.informationRequiredPlaceholder"
                                          )}
                                          size="large"
                                        />
                                      </Form.Item>
                                    </div>
                                    <div className="flex flex-col gap-2">
                                      <div className="text-font&icon-secondary text-xs font-medium font-['DM_Sans'] leading-4 flex items-center justify-between">
                                        <span>{t(
                                          "components.influencerUpgradeForm.instructionLabel"
                                        )}</span>
                                        <UploadImages type="multiple" value={images} onChange={(files: any) => {
                                          form.setFieldValue(['creatorAccountInfoFields', field.name, 'images'], files);
                                        }}>
                                          <div className="flex items-center gap-1 cursor-pointer relative top-1">
                                            <UploadIcon className="w-[20px] h-[20px] text-[#79716B]" />
                                            <div className="justify-start text-[#79716B] text-sm font-semibold font-['DM_Sans'] leading-5">{t('campaignDetail.otherInformationPreview.addImage')}</div>
                                          </div>
                                        </UploadImages>
                                      </div>
                                      <Form.Item name={[field.name, "description"]} className="mb-0">
                                        <Input
                                          placeholder={t(
                                            "components.influencerUpgradeForm.instructionPlaceholder"
                                          )}
                                          size="large"
                                        />
                                      </Form.Item>
                                      {images.length > 0 && (
                                        <div className="flex flex-wrap gap-2 mt-2">
                                          {images.map((item: any, index: number) => (
                                            <div key={item.name + index} className="relative">
                                              <Image
                                                src={item.src}
                                                alt={item.fileName}
                                                width={56}
                                                height={56}
                                                className="object-cover rounded"
                                              />
                                              <div
                                                className="absolute -top-1 -right-1 w-[14px] h-[14px] rounded-full flex items-center justify-center cursor-pointer shadow-sm hover:shadow-md transition-shadow border border-[#A9A29D]"
                                                onClick={() => {
                                                  const newImages = images.filter((_: any, i: number) => i !== index);
                                                  form.setFieldValue(['creatorAccountInfoFields', field.name, 'images'], newImages);
                                                }}
                                              >
                                                <CloseBgIcon className="w-3 h-3 text-white" />
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )
                          })}
                          <Form.Item className="mb-0">
                            <div
                              className="flex items-center text-primary cursor-pointer h-[40px] border border-dashed border-borderColor w-fit px-3 rounded-md"
                              onClick={() => add("")}
                            >
                              <PlusIcon className="text-primary" />
                              <span>{t("components.coreAudiencesForm.add")}</span>
                            </div>
                          </Form.Item>
                        </div>
                      )
                    }}
                  </Form.List>
                  <div className="flex items-center gap-2 mt-4">
                    <Form.Item className="mb-0" name={requireCreatorRegistrationName} valuePropName="checked">
                      <Checkbox />
                    </Form.Item>
                    <div className="justify-start text-Colors-Text-text-secondary-(700) text-sm font-medium font-['DM_Sans'] leading-5">{t('campaignDetail.otherInformationPreview.requireCreatorRegistrationDesc')}</div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
    </div>
  );
}
